import { CartAddEvent } from '@theme/events';

class GasiaCartCrossSell extends HTMLElement {
  static observedAttributes = ['data-product-ids'];

  connectedCallback() {
    this.observer?.disconnect();
    this.observer = new MutationObserver(changes => {
      const container = this.querySelector('.gasia-cross-sell__products');
      if (!container || container.children.length > 0) return;
      const removed = changes.some(change =>
        change.removedNodes.length > 0 &&
        (change.target === container || container.contains(change.target))
      );
      if (removed) this.scheduleLoad();
    });
    this.observer.observe(this, { childList: true, subtree: true });
    this.scheduleLoad();
  }

  attributeChangedCallback() {
    if (this.isConnected) this.scheduleLoad();
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.controller?.abort();
    this.observer?.disconnect();
  }

  scheduleLoad() {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.load(), 150);
  }

  get root() {
    return this.dataset.rootUrl || '/';
  }

  async request(path, options = {}) {
    const response = await fetch(this.root + path, options);
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.description || 'Richiesta non riuscita');
    }
    return data;
  }

  async load() {
    const report = message => {
      this.hidden = false;
      const target = this.querySelector('.gasia-cross-sell__status');
      if (target) {
        target.textContent = 'DIAGNOSI: ' + message;
        target.hidden = true;
         target.style.setProperty('display', 'none', 'important');
      }
    };
    report('lettura del carrello');
    this.controller?.abort();
    this.controller = new AbortController();
    const signal = this.controller.signal;
    try {
      const cart = await this.request('cart.js', { signal });
      const ids = [...new Set(cart.items.map(item => String(item.product_id)))];
      if (!ids.length) {
        report('il carrello ricevuto è vuoto');
        return;
      }
      report('richiesta suggerimenti per ' + ids.length + ' prodotti');
      const results = await Promise.all(ids.slice(0, 5).map(async id => {
        try {
          const data = await this.request(
            `recommendations/products.json?product_id=${id}&limit=10&intent=related`,
            { signal }
          );
          return data.products || [];
        } catch (error) {
          if (error.name === 'AbortError') throw error;
          console.warn('Cross-selling: raccomandazioni non disponibili', id, error);
          return [];
        }
      }));
      if (!this.isConnected || signal.aborted) return;
      const container = this.querySelector('.gasia-cross-sell__products');
      const status = this.querySelector('.gasia-cross-sell__status');
      if (!container || !status) {
        report('contenitore interno non trovato');
        return;
      }
      report('risposta ricevuta: ' + results.flat().length + ' suggerimenti');
      const excluded = new Set(ids);
      const suggestions = [];
      for (let index = 0; index < 10 && suggestions.length < 3; index++) {
        for (const products of results) {
          const product = products[index];
          if (!product || excluded.has(String(product.id))) continue;
          const variant = product.variants?.find(v => v.available && Number(v.price) > 0);
          if (!product.available || !variant) continue;
          excluded.add(String(product.id));
          suggestions.push({ product, variant });
          if (suggestions.length === 3) break;
        }
      }
      const formatter = new Intl.NumberFormat(
        document.documentElement.lang || 'it-IT',
        { style: 'currency', currency: cart.currency }
      );
      const fragment = document.createDocumentFragment();
      for (const { product, variant } of suggestions) {
        const card = document.createElement('div');
        card.className = 'gasia-cross-sell__card';
        const link = document.createElement('a');
        link.href = product.url;
        link.className = 'gasia-cross-sell__link';
        const imageUrl = typeof product.featured_image === 'string'
          ? product.featured_image
          : product.featured_image?.url;
        if (imageUrl) {
          const image = document.createElement('img');
          image.src = imageUrl;
          image.alt = product.title;
          image.width = 72;
          image.height = 72;
          image.loading = 'lazy';
          link.append(image);
        }
        const title = document.createElement('span');
        title.textContent = product.title;
        link.append(title);
        const price = document.createElement('span');
        price.className = 'gasia-cross-sell__price';
        price.textContent = formatter.format(variant.price / 100);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'button gasia-cross-sell__add';
        button.textContent = 'Aggiungi';
        button.setAttribute('aria-label', `Aggiungi ${product.title}`);
        button.addEventListener('click', () => this.add(variant, product, button));
        card.append(link, price, button);
        fragment.append(card);
      }
      container.replaceChildren(fragment);
      report('ricevuti ' + results.flat().length + ', selezionati ' + suggestions.length + ', schede presenti ' + container.children.length);
    } catch (error) {
      report(error.name + ': ' + error.message);
      console.error('Cross-selling: caricamento non riuscito', error);
    }
  }

  async add(variant, product, button) {
    const status = this.querySelector('.gasia-cross-sell__status');
    status.hidden = false;
    status.style.removeProperty('display');
    button.disabled = true;
    button.textContent = 'Aggiunta…';
    status.textContent = '';
    let added = false;
    try {
      const sections = [...new Set(
        [...document.querySelectorAll('cart-items-component[data-section-id]')]
          .map(element => element.dataset.sectionId)
      )].filter(Boolean).slice(0, 5);
      const payload = {
        items: [{ id: variant.id, quantity: 1 }],
        sections_url: window.location.pathname,
      };
      if (sections.length) payload.sections = sections;
      const result = await this.request('cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      added = true;
      const cart = await this.request('cart.js');
      document.dispatchEvent(new CartAddEvent(cart, 'gasia-cross-sell', {
        source: 'gasia-cross-sell',
        productId: String(product.id),
        variantId: String(variant.id),
        itemCount: cart.item_count,
        sections: result.sections || {},
      }));
      if (this.isConnected) {
        status.textContent = 'Prodotto aggiunto al carrello.';
        this.scheduleLoad();
      }
    } catch (error) {
      console.error('Cross-selling: aggiornamento non riuscito', error);
      if (added) {
        status.textContent = 'Prodotto aggiunto. Ricarica la pagina per aggiornare il carrello.';
        button.textContent = 'Aggiunto';
      } else {
        status.textContent = 'Non è stato possibile aggiungere il prodotto. Riprova.';
        button.disabled = false;
        button.textContent = 'Aggiungi';
      }
    }
  }
}

if (!customElements.get('gasia-cart-cross-sell')) {
  customElements.define('gasia-cart-cross-sell', GasiaCartCrossSell);
}
